import { useState, MouseEvent, ChangeEvent } from 'react'
import type { Book, SortConfig, BookField, FieldVisibility } from '@/types'
import { formatAuthors, formatCategories } from '../../formattingUtils'
import { getFieldConfigArray } from '../../constants/visibilityConfig'
import VisibilityConfigDropdown from '../VisibilityConfigDropdown'

interface BookWithProfile extends Book {
    profiles?: {
        full_name?: string
    }
}

type ColumnValue = string | string[] | number | undefined

interface ExtendedListViewProps {
    books: BookWithProfile[]
    selectedIds: Set<string>
    allFilteredSelected: boolean
    toggleBookSelection: (e: MouseEvent | ChangeEvent, bookId: string) => void
    onBookClick: (book: BookWithProfile) => void
    initiateDelete: (e: MouseEvent, bookId: string) => void
    deselectAll: () => void
    selectAllFiltered: () => void
    isBiblioverso: boolean
    isSharedView: boolean
    sortConfig: SortConfig
    handleSort: (key: BookField) => void
    visibility: FieldVisibility
    toggleField: (field: keyof FieldVisibility) => void
    resetToDefaults: () => void
    allowedFields: string[] | null
}

export default function ExtendedListView({
    books,
    selectedIds,
    allFilteredSelected,
    toggleBookSelection,
    onBookClick,
    initiateDelete,
    deselectAll,
    selectAllFiltered,
    isBiblioverso,
    isSharedView,
    sortConfig,
    handleSort,
    visibility,
    toggleField,
    resetToDefaults,
    allowedFields,
}: Readonly<ExtendedListViewProps>) {
    const [showConfig, setShowConfig] = useState(false)

    const fieldConfig = getFieldConfigArray(visibility)

    const COLUMNS: ReadonlyArray<{ id: keyof FieldVisibility; label: string; format?: (v: ColumnValue) => string }> = [
        { id: 'language', label: 'Idioma' },
        { id: 'category', label: 'Categoría', format: (v) => formatCategories(v as string[]) || '-' },
        { id: 'publisher', label: 'Editorial' },
        { id: 'series', label: 'Serie' },
        { id: 'series_number', label: '#' },
        { id: 'format', label: 'Formato' },
        { id: 'status', label: 'Estado' },
        { id: 'progress', label: 'Progreso' },
        { id: 'location', label: 'Ubicación' },
    ]

    const sortArrow = sortConfig.direction === 'asc' ? ' ↑' : ' ↓'

    const renderHeader = (id: string, label: string, sortKey: string = id) => {
        const isVisible = Object.prototype.hasOwnProperty.call(visibility, id) && visibility[id as keyof FieldVisibility]
        if (!isVisible) return null
        const isActive = sortConfig.key === sortKey

        return (
            <th
                key={id}
                className={`sortable-th ${isActive ? 'active' : ''}`}
                onClick={() => handleSort(sortKey as BookField)}
            >
                {label}
                {isActive && sortArrow}
            </th>
        )
    }

    const renderCell = (
        book: BookWithProfile,
        id: keyof FieldVisibility,
        format: (v: ColumnValue) => string = (v) => String(v || '-')
    ) => {
        const isVisible = Object.prototype.hasOwnProperty.call(visibility, id) && visibility[id]
        if (!isVisible) return null
        const value = (book as unknown as Record<string, unknown>)[id] as ColumnValue
        return <td key={id}>{format(value)}</td>
    }

    return (
        <div className="books-table-wrapper animate-fade-in">
            <table className="books-table extended-table">
                <thead>
                    <tr>
                        <th className="th-checkbox">
                            <input
                                type="checkbox"
                                checked={allFilteredSelected}
                                onChange={() => (allFilteredSelected ? deselectAll() : selectAllFiltered())}
                            />
                        </th>
                        {isBiblioverso && (
                            <th
                                className={`sortable-th ${(sortConfig.key as string) === 'owner' ? 'active' : ''}`}
                                onClick={() => handleSort('owner' as BookField)}
                            >
                                Biblionauta {(sortConfig.key as string) === 'owner' && sortArrow}
                            </th>
                        )}
                        {renderHeader('title', 'Título')}
                        {renderHeader('author', 'Autor')}
                        {COLUMNS.map((col) => renderHeader(col.id, col.label))}
                        <th className="th-actions">
                            <button
                                className="settings-icon-btn"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setShowConfig(!showConfig)
                                }}
                                title="Personalizar campos"
                            >
                                ⚙️
                            </button>
                            {showConfig && (
                                <VisibilityConfigDropdown
                                    fields={fieldConfig}
                                    onToggle={toggleField}
                                    onClose={() => setShowConfig(false)}
                                    isSharedView={isSharedView}
                                    allowedFields={allowedFields}
                                    position="bottom-right"
                                    onReset={resetToDefaults}
                                />
                            )}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {books.map((book) => {
                        const isSelected = selectedIds.has(book.id)
                        return (
                            <tr key={book.id} className={`table-row clickable ${isSelected ? 'is-selected' : ''}`} onClick={() => onBookClick(book)} data-testid={`book-card-${book.title}`}>
                                <td className="td-checkbox" onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => toggleBookSelection(e, book.id)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </td>
                                {isBiblioverso && <td className="td-owner">{book.profiles?.full_name || 'Mi Biblioteca'}</td>}
                                {visibility.title && <td className="td-title">{book.title}</td>}
                                {visibility.author && <td className="td-author">{formatAuthors(book.author)}</td>}
                                {COLUMNS.map((col) => renderCell(book, col.id, col.format))}
                                <td className="td-actions" onClick={(e) => e.stopPropagation()}>
                                    {!isBiblioverso && !isSharedView && (
                                        <button onClick={(e) => initiateDelete(e, book.id)} className="table-delete" data-testid="delete-book-button">
                                            🗑️
                                        </button>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}
